import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Col, Empty, Row, Segmented, Spin, Typography } from 'antd';
import ToolCard from '../components/ToolCard';
import { rankingsAPI } from '../services/api';
import { useLanguage } from '../store/LanguageContext';
import { usePageSeo } from '../utils/usePageSeo';

const { Title, Paragraph } = Typography;

const RankingsPage: React.FC = () => {
  const { list = 'popular' } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const zh = language === 'zh-CN';
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState<any>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    rankingsAPI
      .get(list)
      .then((res) => {
        if (!cancelled) setPayload(res.data);
      })
      .catch(() => {
        if (!cancelled) setPayload(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [list]);

  const meta = payload?.meta;
  const title = meta ? (zh ? meta.title_zh : meta.title_en) : list;
  const blurb = meta ? (zh ? meta.blurb_zh : meta.blurb_en) : '';
  const allowed: string[] = payload?.allowed || (zh ? ['popular', 'free', 'cn-direct', 'new'] : ['popular', 'free', 'new']);

  usePageSeo({
    title,
    description: blurb || title,
    path: `/rankings/${list}`,
  });

  const options = useMemo(
    () =>
      allowed.map((l) => ({
        label:
          l === 'popular' ? (zh ? '热门' : 'Popular')
            : l === 'free' ? (zh ? '免费' : 'Free')
              : l === 'cn-direct' ? (zh ? '国内直连' : 'CN direct')
                : l === 'new' ? (zh ? '新品' : 'New')
                  : l,
        value: l,
      })),
    [allowed, zh],
  );

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const tools = payload?.tools || [];

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px 48px' }}>
      <Segmented
        options={options}
        value={list}
        onChange={(v) => navigate(`/rankings/${v}`)}
        style={{ marginBottom: 20 }}
      />
      <Title level={2}>{title}</Title>
      {blurb ? <Paragraph type="secondary">{blurb}</Paragraph> : null}
      {tools.length === 0 ? (
        <Empty description={zh ? '暂无数据' : 'No tools'} />
      ) : (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          {tools.map((tool: any) => (
            <Col key={tool.id || tool.slug} xs={24} sm={12} md={8} lg={6}>
              <ToolCard tool={tool} />
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default RankingsPage;
